import React, { useState } from "react";
import {
  carrierAddState,
  carrierContactPerState,
  carrierData,
  carrierEditState,
  carrierMetricPerState,
  carrierStateAppointed,
  carrierStates,
  defaultCarrierData,
  filteredCarrierData,
  isCarrierFiltered,
  selectedCarriers,
} from "../atom/atoms";
import { useRecoilState } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import {
  ICarrierContactPerState,
  ICarriers,
  INSAContacts,
  INationalContacts,
  ISPLCContacts,
  IStatesOptions,
  ICarrierMetricsPerState,
  ICarrierStateAppointed,
} from "../types";
import { FormProvider, useForm } from "react-hook-form";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import CarrierDetails from "./CarrierDetails";
import CarrierNationalContact from "./CarrierNationalContact";
import CarrierSPLCContact from "./CarrierSPLCContact";
import CarrierNSAContact from "./CarrierNSAContact";
import CarrierContactPerState from "./CarrierContactPerState";
import { toast } from "../../../../components/toastNotification";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from "react-query";
import useAddCarrier from "../hooks/useAddCarrier";
import { useGetCarrierPhoneType } from "../hooks/useGetCarrierPhoneType";

import useEditCarrier from "../hooks/useEditCarrier";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import useSendEmail from "../hooks/useSendEmail";
import { Disclosure } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import CarrierMetricsPerState from "./CarrierMetricsPerState";
import CarrierStateAppointed from "./CarrierStateAppointed";
interface CarrierFormProps {
  refetchGetCarriers: (
    options?: (RefetchOptions & RefetchQueryFilters<ICarriers[]>) | undefined
  ) => Promise<QueryObserverResult<ICarriers[], unknown>>;
}
const CarrierForm = ({ refetchGetCarriers }: CarrierFormProps): JSX.Element => {
  const [addStatus, setAddStatus] = useRecoilState(carrierAddState);
  const [editStatus, setEditStatus] = useRecoilState(carrierEditState);
  const [, setSelectedStates] =
    useRecoilState<Array<IStatesOptions>>(carrierStates);
  const [, setCarrierContactPerState] = useRecoilState<
    Array<ICarrierContactPerState>[]
  >(carrierContactPerState);
  const [metrics, setCarrierMetricsPerState] = useRecoilState<
    Array<ICarrierMetricsPerState>[]
  >(carrierMetricPerState);
  const [, setCarrierStateAppointed] = useRecoilState<
    Array<ICarrierStateAppointed>[]
  >(carrierStateAppointed);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [carrierForEdit, setCarrierForEdit] = useRecoilState(carrierData);
  const [carrierToSave, setCarrierToSave] = useState<ICarriers>();
  const [, setFilteredCarrier] =
    useRecoilState<Array<ICarriers>>(filteredCarrierData);
  const [isFiltered] = useRecoilState<true | false>(isCarrierFiltered);
  const [selectedCarrierIds] =
    useRecoilState<Array<IStatesOptions>>(selectedCarriers);

  const [isFileDelete, setIsFileDelete] = useState(false);
  const { mutateAsync: sendCarrierEmail } = useSendEmail();

  const useFormMethods = useForm<ICarriers>({
    defaultValues: { ...carrierForEdit },
  });
  const {
    formState: { isDirty },
    watch,
    setValue,
    reset,
  } = useFormMethods;
  //Add new carrier function
  const { mutate: addNewCarrier } = useAddCarrier();
  //Edit Carrier Mutation
  const { mutate: editCarrier } = useEditCarrier();

  const resetFields = () => {
    reset(defaultCarrierData);
    setCarrierForEdit(defaultCarrierData);
    setSelectedStates([]);
    setCarrierContactPerState([]);
    setCarrierMetricsPerState([]);
    setCarrierStateAppointed([]);
  };
  const handleCancel = () => {
    resetFields();
    addStatus ? setAddStatus(false) : setEditStatus(false);
  };
  const onSubmit = (data: ICarriers) => {
    if (isDirty === true || isFileDelete) {
      contactsDupChecker(data);
    } else {
      toast.fire("Saving Failed", "No changes has been made", "warning");
    }
  };

  const contactsDupChecker = (data: ICarriers) => {
    let hasDuplicates = false;
    //Check ICarrierContactPerState duplicates and setting CarrierContactPerState data
    const stateContacts = [] as Array<ICarrierContactPerState>;
    const stateMetrics = [] as Array<ICarrierMetricsPerState>;
    const stateAppointed = [] as Array<ICarrierStateAppointed>;

    if (data.states && data.states?.length > 0) {
      watch("states")?.forEach((state) => {
        data?.[`contactState_${state.label}`]?.forEach(
          (contact: ICarrierContactPerState) => {
            const sContactValues = stateContacts.map((s) => {
              return {
                contactType: s.contactType ?? null,
                name: s.name ?? "",
                email: s.email ?? "",
                phoneTypeId: s.phoneTypeId ?? null,
                contactNumber: s.contactNumber ?? "",
                extensionNumber: s.extensionNumber ?? null,
                stateId: s.stateId,
              };
            });
            const contactValues = {
              contactType: contact.contactType ?? null,
              name: contact.name ?? "",
              email: contact.email ?? "",
              phoneTypeId: contact.phoneTypeId ?? null,
              contactNumber: contact.contactNumber ?? "",
              extensionNumber: contact.extensionNumber ?? null,
              stateId: state.value,
            };
            //TEST ALL TABLES FOR DUPLICATE
            //CONTINUE REFRESH IN FILTER MODE
            if (
              sContactValues.some(
                (e) =>
                  Object.entries(e).toString() ===
                  Object.entries(contactValues).toString()
              )
            ) {
              hasDuplicates = true;
              toast.fire(
                "Duplicate Contact Row",
                "Contact Per State has duplicate rows",
                "error"
              );
            } else {
              stateContacts.push({
                ...contact,
                carrierStatesId:
                  data?.[`contactState_${state.label}`][0].carrierStatesId,
                stateCode: state?.label ?? null,
                stateId: state?.value ?? null,
              });
            }
          }
        );
        data?.[`metricState_${state.label}`]?.forEach(
          (metrics: ICarrierMetricsPerState) => {
            const sMetricsValue = stateMetrics.map((s) => {
              return {
                totalPIFCount: s.totalPifCount ?? null,
                totalPIFPrem: s.totalPifPremium ?? null,
                newBusiness: s.newBusinessPremium ?? null,
                renewalPrem: s.renewalPremium ?? null,
                state: s.state ?? "",
                stateId: s.stateId,
              };
            });
            const metricsValue = {
              totalPIFCount: metrics.totalPifCount ?? null,
              totalPIFPrem: metrics.totalPifPremium ?? null,
              newBusiness: metrics.newBusinessPremium ?? null,
              renewalPrem: metrics.renewalPremium ?? null,
              state: metrics.state ?? "",
              stateId: metrics.stateId,
            };

            if (
              sMetricsValue.some(
                (e) =>
                  Object.entries(e).toString() ===
                  Object.entries(metricsValue).toString()
              )
            ) {
              hasDuplicates = true;
              toast.fire(
                "Duplicate Contact Row",
                "Contact Per State has duplicate rows",
                "error"
              );
            } else {
              stateMetrics.push({
                ...metrics,
                carrierStatesId:
                  data?.[`metricState_${state.label}`][0].carrierStatesId,
                state: state?.label ?? null,
                stateId: state?.value ?? null,
              });
            }
          }
        );
        data?.[`stateAppointed_${state?.label}`]?.forEach(
          (appoint: ICarrierStateAppointed) => {
            const sAppointedValues = stateAppointed.map((s) => {
              return {
                newBusiness: s.newBusiness ?? null,
                renewalBusiness: s.renewalBusiness ?? "",
                newBusinessSuspended: s.newBusinessSuspended ?? "",
                newBusinessSuspendedDate: s.newBusinessSuspendedDate ?? null,
                stateId: s.stateId,
              };
            });
            const contactValues = {
              newBusiness: appoint.newBusiness ?? null,
              renewalBusiness: appoint.renewalBusiness ?? "",
              newBusinessSuspended: appoint.newBusinessSuspended ?? "",
              newBusinessSuspendedDate:
                appoint.newBusinessSuspendedDate ?? null,
              stateId: state.value,
            };
            if (
              sAppointedValues.some(
                (e) =>
                  Object.entries(e).toString() ===
                  Object.entries(contactValues).toString()
              )
            ) {
              hasDuplicates = true;
              toast.fire(
                "Duplicate State Appointed Row",
                "State Appointed has duplicate rows",
                "error"
              );
            } else {
              stateAppointed.push({
                ...appoint,
                carrierStatesId:
                  data?.[`stateAppointed_${state.label}`][0].carrierStatesId,
                states: state?.label ?? null,
                stateId: state?.value ?? null,
              });
            }
          }
        );
      });
      setValue("carrierStateAppointed", stateAppointed);
      setValue("carrierContactPerState", stateContacts);
      setValue("carrierMetricsPerState", stateMetrics);
    }

    //Check INationalContacts duplicates
    if (data.nationalContacts) {
      const natContacts = [] as Array<INationalContacts>;
      data?.nationalContacts.forEach((nat) => {
        const nationalsC = natContacts.map((s) => {
          return {
            contactType: s.contactType ?? null,
            name: s.name ?? "",
            email: s.email ?? "",
            phoneTypeId: s.phoneTypeId ?? null,
            contactNumber: s.contactNumber ?? "",
            extensionNumber: s.extensionNumber ?? null,
          };
        });
        const nsaObject = {
          contactType: nat.contactType ?? null,
          name: nat.name ?? "",
          email: nat.email ?? "",
          phoneTypeId: nat.phoneTypeId ?? null,
          contactNumber: nat.contactNumber ?? "",
          extensionNumber: nat.extensionNumber ?? null,
        };
        if (
          nationalsC.some(
            (e) =>
              Object.entries(e).toString() ===
              Object.entries(nsaObject).toString()
          )
        ) {
          hasDuplicates = true;
          return toast.fire(
            "Duplicate Contact Row",
            "National Contact has duplicate rows",
            "error"
          );
        } else {
          natContacts.push(nat);
        }
      });
    }

    //Check INSAContacts duplicates
    if (data.nsaContacts) {
      const nsaContactRows = [] as Array<INSAContacts>;
      data?.nsaContacts.forEach((nsa) => {
        const nsaContacts = nsaContactRows.map((s) => {
          return {
            contactType: s.contactType ?? null,
            name: s.name ?? "",
            email: s.email ?? "",
            phoneTypeId: s.phoneTypeId ?? null,
            contactNumber: s.contactNumber ?? "",
            extensionNumber: s.extensionNumber ?? null,
          };
        });
        const nsaObject = {
          contactType: nsa.contactType ?? null,
          name: nsa.name ?? "",
          email: nsa.email ?? "",
          phoneTypeId: nsa.phoneTypeId ?? null,
          contactNumber: nsa.contactNumber ?? "",
          extensionNumber: nsa.extensionNumber ?? null,
        };
        if (
          nsaContacts.some(
            (e) =>
              Object.entries(e).toString() ===
              Object.entries(nsaObject).toString()
          )
        ) {
          hasDuplicates = true;
          toast.fire(
            "Duplicate Contact Row",
            "NSA Contact has duplicate rows",
            "error"
          );
        } else {
          nsaContactRows.push(nsa);
        }
      });
    }
    //Check ISPLCContacts duplicates
    if (data.splcContacts) {
      const splcContactRows = [] as Array<ISPLCContacts>;
      data?.splcContacts.forEach((splc) => {
        const splcContacts = splcContactRows.map((s) => {
          return {
            contactType: s.contactType ?? null,
            name: s.name ?? "",
            email: s.email ?? "",
            phoneTypeId: s.phoneTypeId ?? null,
            contactNumber: s.contactNumber ?? "",
            extensionNumber: s.extensionNumber ?? null,
          };
        });
        const splcObject = {
          contactType: splc.contactType ?? null,
          name: splc.name ?? "",
          email: splc.email ?? "",
          phoneTypeId: splc.phoneTypeId ?? null,
          contactNumber: splc.contactNumber ?? "",
          extensionNumber: splc.extensionNumber ?? null,
        };
        if (
          splcContacts.some(
            (e) =>
              Object.entries(e).toString() ===
              Object.entries(splcObject).toString()
          ) // true
        ) {
          hasDuplicates = true;
          return toast.fire(
            "Duplicate Contact Row",
            "SPLC Contact has duplicate rows",
            "error"
          );
        } else {
          splcContactRows.push(splc);
        }
      });
    }
    //Check ICarrierMetricsPerState duplicates
    if (watch("carrierMetricsPerState")) {
      const cmPerState = [] as Array<ICarrierMetricsPerState>;
      watch("carrierMetricsPerState")?.forEach((row) => {
        const metricsState = cmPerState.map((s) => {
          return {
            totalPIFCount: s.totalPifCount ?? null,
            totalPIFPrem: s.totalPifPremium ?? null,
            newBusiness: s.newBusinessPremium ?? null,
            renewalPrem: s.renewalPremium ?? null,
            state: s.state ?? "",
            stateId: s.stateId,
          };
        });
        const cmpsObject = {
          totalPIFCount: row.totalPifCount ?? null,
          totalPIFPrem: row.totalPifPremium ?? null,
          newBusiness: row.newBusinessPremium ?? null,
          renewalPrem: row.renewalPremium ?? null,
          state: row.state ?? "",
          stateId: row.stateId,
        };

        if (
          metricsState.some(
            (e) =>
              Object.entries(e).toString() ===
              Object.entries(cmpsObject).toString()
          )
        ) {
          hasDuplicates = true;
          return toast.fire(
            "Duplicate Contact Row",
            "Metric Per State has duplicate rows",
            "error"
          );
        } else {
          cmPerState.push(row);
        }
      });
    }

    if (hasDuplicates === false) {
      setCarrierToSave({
        ...data,
        carrierContactPerState: stateContacts,
        carrierMetricsPerState: stateMetrics,
        carrierStateAppointed: stateAppointed,
      });
      setShowSaveModal(true);
    }
  };

  const saveCarrier = (data: ICarriers | undefined = carrierToSave) => {
    setIsSaving(true);
    if (addStatus) {
      addNewCarrier(
        { ...data },
        {
          onSuccess: (res) => {
            if (res) {
              if (res.status === 200 || res.status === 201) {
                sendCarrierEmail(res.data, {
                  onSuccess: (res) => {
                    if (res) {
                      refetchGetCarriers().then((result) => {
                        if (result.isSuccess && result.data) {
                          if (res.status === 400) {
                            toast.fire(
                              "Success",
                              "Carrier added successfully, but failed in sending email",
                              "warning"
                            );
                          } else {
                            setIsSaving(false);
                            toast.fire(
                              "Success",
                              "Carrier added successfully",
                              "success"
                            );
                          }
                          resetFields();
                          setAddStatus(false);
                          setIsSaving(false);
                        }
                      });
                    }
                  },
                });
              } else if (res.status !== 200 && res.data === "duplicate") {
                setIsSaving(false);
                toast.fire("Failed ", "Carrier already exists", "error");
              } else if (res.status !== 200 && res.data !== "duplicate") {
                setIsSaving(false);
                toast.fire(
                  "Failed",
                  "Server Error: Cannot add new Carrier",
                  "error"
                );
              }
            } else {
              setIsSaving(false);
              toast.fire(
                "Failed",
                "Server Error: Cannot add new Carrier",
                "error"
              );
            }
          },
          onError: (error: any) => {
            setIsSaving(false);
            if (
              error.response.data.message === "Failed sending of email" ||
              error.response.data.message === "Failed inserting of notification"
            ) {
              toast.fire(
                "Success",
                "New Carrier added successfully, but " +
                  error.response.data.message,
                "warning"
              );
            } else {
              toast.fire(
                "Failed",
                "Server Error: Cannot add new Carrier",
                "error"
              );
            }
          },
        }
      );
    } else if (editStatus) {
      editCarrier(
        { ...data },
        {
          onSuccess: (res: any) => {
            if (res) {
              if (res.status === 200 || res.status === 201) {
                sendCarrierEmail(res.data, {
                  onSuccess: (res) => {
                    if (res) {
                      //Refresh filtered data after every update
                      refetchGetCarriers().then((result) => {
                        if (result.isSuccess && result.data) {
                          if (isFiltered) {
                            const carrierIds = selectedCarrierIds.map(
                              (x) => x.value
                            ) as [number | null];
                            const filtered = result.data?.filter(
                              (carrier: ICarriers) =>
                                carrierIds.includes(carrier.masterCarrierId)
                            );
                            filtered &&
                              setFilteredCarrier(
                                filtered.map((data) => ({ ...data }))
                              );
                          }
                          if (res.status === 400) {
                            toast.fire(
                              "Success",
                              "Carrier updated successfully, but failed in sending email",
                              "warning"
                            );
                          } else {
                            toast.fire(
                              "Success",
                              "Carrier updated successfully",
                              "success"
                            );
                          }
                          resetFields();
                          setEditStatus(false);
                          setIsSaving(false);
                        }
                      });
                    }
                  },
                });
              } else {
                if (res.status === 400 && res.data === "duplicate") {
                  setIsSaving(false);
                  toast.fire("Failed ", "Carrier already exists", "error");
                } else {
                  setIsSaving(false);
                  toast.fire(
                    "Server Error",
                    "Failed to update Carrier",
                    "error"
                  );
                }
              }
            } else if (res.status !== 200 && res.data === "duplicate") {
              setIsSaving(false);
              toast.fire("Failed ", "Carrier already exists", "error");
            } else if (res.status !== 200 && res.data !== "duplicate") {
              setIsSaving(false);
              toast.fire(
                "Failed",
                "Server Error: Cannot add new Carrier",
                "error"
              );
            } else {
              toast.fire("Server Error", "Failed to update Carrier", "error");
            }
          },
          onError: (error) => {
            setIsSaving(false);
            toast.fire("Server Error", "Failed to update Carrier", "error");
          },
        }
      );
    }
  };

  return (
    <>
      {(addStatus || editStatus) && (
        <div className="px-4 pb-4 pt-3 border-l-2 border-primary  box-shadow">
          <FormProvider {...useFormMethods}>
            <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
              <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg">
                <div className="text-base text-primary font-bold">
                  {addStatus
                    ? "Add Carrier"
                    : "Edit Carrier " + carrierForEdit.masterCarrierId}
                </div>
                <div className="flex justify-between pb-1 gap-2">
                  <SubmitHookForm
                    type="submit"
                    disabled={isSaving}
                    buttonText={isSaving ? "Saving.." : "Save"}
                    onClick={undefined}
                  />
                  <CancelButton
                    onClick={() => {
                      handleCancel();
                    }}
                  />
                </div>
              </div>
              <div className="pb-1 h-0.5 border-b-2 border-secondary ">
                <div className="pt-4 text-xs text-secondary italic font-normal">
                  <b> Note:</b> All fields with * are required to fill.
                </div>
              </div>
              <CarrierDetails setIsFileDelete={setIsFileDelete} />
              {watch("states")?.length! > 0 && <CarrierMetricsPerState />}
              <div className="px-4 pb-4 my-2 bg-white rounded-lg box-shadow">
                <div className="text-quinary font-bold text-sm font-sans py-2">
                  Contact Details
                </div>
                <CarrierNationalContact
                  nationalContacts={
                    carrierForEdit.nationalContacts &&
                    carrierForEdit.nationalContacts?.length > 0
                      ? carrierForEdit.nationalContacts
                      : undefined
                  }
                />
                {watch("masterCarrierDetails.nsa") && (
                  <CarrierNSAContact
                    nsaContacts={
                      carrierForEdit.nsaContacts &&
                      carrierForEdit.nsaContacts.length > 0
                        ? carrierForEdit.nsaContacts
                        : undefined
                    }
                  />
                )}
                {watch("masterCarrierDetails.splc") && (
                  <CarrierSPLCContact
                    splcContacts={
                      carrierForEdit.splcContacts &&
                      carrierForEdit.splcContacts.length > 0
                        ? carrierForEdit.splcContacts
                        : undefined
                    }
                  />
                )}
                {watch("states")?.length! > 0 && <CarrierStateAppointed />}
                {watch("states")?.length! > 0 && <CarrierContactPerState />}
              </div>
            </form>
          </FormProvider>
        </div>
      )}

      {showSaveModal && (
        <ConfirmBoxModal
          ModalTitle={editStatus ? "Update Carrier" : "Save New Carrier"}
          qConfirmation={
            editStatus
              ? "Are you sure you want to update this?"
              : "Are you sure you want to save this?"
          }
          isOpen={showSaveModal}
          onClose={() => {
            setShowSaveModal(false);
          }}
          onClickFunction={() => {
            setShowSaveModal(false);
            saveCarrier(carrierToSave);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default CarrierForm;
